/**
 * RequisitionClosureSettingsSection Component
 *
 * When a requisition counts as completed and whether and when that archives it.
 * Stored on every change.
 */
import { useMutation, useQuery } from '@apollo/client/react';
import { useEffect, useState } from 'react';
import {
  DEFAULT_REQUISITION_COMPLETION,
  REQUISITION_COMPLETION_BASE_STATUSES,
  type RequisitionCompletionBaseStatus
} from '@insio/shared-types';
import { pick } from 'es-toolkit';
import { AlertBanner, Loader } from '@insio/ui-toolkit';
import {
  RequisitionAutomationSettingsDocument,
  UpdateRequisitionAutomationSettingsDocument
} from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { CompletionSettingsForm, type CompletionSettingsValue } from './CompletionSettingsForm';
import { useCanChangeAutomationSettings } from './use-can-change-automation-settings';

type Settings = CompletionSettingsValue<RequisitionCompletionBaseStatus>;

export function RequisitionClosureSettingsSection() {
  const t = useFeatureTranslation('systemSettings.automation.requisitionClosure');
  const { showError } = useToastHelpers();
  const canChange = useCanChangeAutomationSettings();

  const { data, loading, error } = useQuery(RequisitionAutomationSettingsDocument);
  // The response carries no id to normalise, so the settings query is refetched.
  const [updateSettings] = useMutation(UpdateRequisitionAutomationSettingsDocument, {
    refetchQueries: [{ query: RequisitionAutomationSettingsDocument }],
    awaitRefetchQueries: true
  });

  const [draft, setDraft] = useState<Settings>({
    completionBaseStatus: DEFAULT_REQUISITION_COMPLETION.baseStatus,
    isDeliveryRequired: DEFAULT_REQUISITION_COMPLETION.isDeliveryRequired,
    isInvoicingRequired: DEFAULT_REQUISITION_COMPLETION.isInvoicingRequired,
    isArchivingOnCompletion: DEFAULT_REQUISITION_COMPLETION.isArchivingOnCompletion,
    archiveAfterDays: DEFAULT_REQUISITION_COMPLETION.archiveAfterDays
  });

  const settings = data?.requisitionAutomationSettings;

  useEffect(() => {
    if (settings) {
      setDraft({
        completionBaseStatus:
          REQUISITION_COMPLETION_BASE_STATUSES.find(
            status => status === settings.completionBaseStatus
          ) ?? DEFAULT_REQUISITION_COMPLETION.baseStatus,
        isDeliveryRequired: settings.isDeliveryRequired,
        isInvoicingRequired: settings.isInvoicingRequired,
        isArchivingOnCompletion: settings.isArchivingOnCompletion,
        archiveAfterDays: settings.archiveAfterDays
      });
    }
  }, [settings]);

  /** Stored the moment it changes; a refused write puts the choice back. */
  const handleChange = async (change: Partial<Settings>) => {
    if (!canChange) return;
    const reverted = pick(draft, Object.keys(change) as (keyof Settings)[]);
    setDraft(current => ({ ...current, ...change }));
    try {
      // errorPolicy 'none' (vs global 'all') makes GraphQL errors throw into the catch below
      await updateSettings({ variables: { input: change }, errorPolicy: 'none' });
    } catch {
      setDraft(current => ({ ...current, ...reverted }));
      showError(t('saveError'));
    }
  };

  if (loading && !settings) {
    return <Loader.Section />;
  }

  if (error) {
    return <AlertBanner variant='error' title={t('loadError')} />;
  }

  return (
    <CompletionSettingsForm
      namespace='requisitionClosure'
      baseStatuses={REQUISITION_COMPLETION_BASE_STATUSES}
      value={draft}
      onChange={handleChange}
      disabled={!canChange}
      testIdPrefix='requisition-closure'
    />
  );
}
